import InfoIcon from "#/assets/exam-results/info.svg?url";
import { DqModal } from "#/components/Question/DqModal";
import { ExamDifficultyLevels } from "#/constants";
import type {
  EducationPackExamQuestionDetail,
  FindSupplementaryExamResult
} from "#/services/rest/gwt/exam-results/exam-results.types";
import { useBreakLG } from "@monta/design-system";
import clsx from "clsx";
import { truncate } from "lodash";
import { useMemo, useState } from "react";
import QStatus from "../QStatus";
import styles from "./styles.module.scss";

export const QLevel = (props: { level: number }) => {
  const { level } = props;

  return (
    <div className={styles["level__container"]}>
      {level === 1 ? (
        <>
          <div className={clsx(styles["level__bar"], styles["level--easy"])} />
          <div className={clsx(styles["level__bar"], styles["level--easy"])} />
          <div className={clsx(styles["level__bar"], styles["level--easy"])} />
          <div
            className={clsx(styles["level__bar"], styles["level--neutral"])}
          />
          <div
            className={clsx(styles["level__bar"], styles["level--neutral"])}
          />
          <div
            className={clsx(styles["level__bar"], styles["level--neutral"])}
          />
          <div
            className={clsx(styles["level__bar"], styles["level--neutral"])}
          />
          <div
            className={clsx(styles["level__bar"], styles["level--neutral"])}
          />
          <div
            className={clsx(styles["level__bar"], styles["level--neutral"])}
          />
        </>
      ) : level === 2 ? (
        <>
          <div
            className={clsx(styles["level__bar"], styles["level--medium"])}
          />
          <div
            className={clsx(styles["level__bar"], styles["level--medium"])}
          />
          <div
            className={clsx(styles["level__bar"], styles["level--medium"])}
          />
          <div
            className={clsx(styles["level__bar"], styles["level--medium"])}
          />
          <div
            className={clsx(styles["level__bar"], styles["level--medium"])}
          />
          <div
            className={clsx(styles["level__bar"], styles["level--medium"])}
          />
          <div
            className={clsx(styles["level__bar"], styles["level--neutral"])}
          />
          <div
            className={clsx(styles["level__bar"], styles["level--neutral"])}
          />
          <div
            className={clsx(styles["level__bar"], styles["level--neutral"])}
          />
        </>
      ) : (
        <>
          <div className={clsx(styles["level__bar"], styles["level--hard"])} />
          <div className={clsx(styles["level__bar"], styles["level--hard"])} />
          <div className={clsx(styles["level__bar"], styles["level--hard"])} />
          <div className={clsx(styles["level__bar"], styles["level--hard"])} />
          <div className={clsx(styles["level__bar"], styles["level--hard"])} />
          <div className={clsx(styles["level__bar"], styles["level--hard"])} />
          <div className={clsx(styles["level__bar"], styles["level--hard"])} />
          <div className={clsx(styles["level__bar"], styles["level--hard"])} />
          <div className={clsx(styles["level__bar"], styles["level--hard"])} />
        </>
      )}
    </div>
  );
};

const ResultsQuestion = (
  props: EducationPackExamQuestionDetail | FindSupplementaryExamResult
) => {
  const {
    difficultyLevel,
    mainTopic,
    subTopic,
    selectedChoiceOrder,
    questionOrder,
    correctChoiceOrder,
    questionID
  } = props as FindSupplementaryExamResult;

  const { questionId } = props as EducationPackExamQuestionDetail;
  const isDesktop = useBreakLG();
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [questionModalId, setQuestionModalId] = useState<null | number>(null);

  const level = useMemo(() => {
    if (
      difficultyLevel === ExamDifficultyLevels["academic"] ||
      difficultyLevel === ExamDifficultyLevels["easy"]
    ) {
      return 1;
    } else if (difficultyLevel === ExamDifficultyLevels["normal"]) {
      return 2;
    } else {
      return 3;
    }
  }, [difficultyLevel]);

  const status = useMemo(() => {
    if (!selectedChoiceOrder) return "اصلا نزدی";
    else if (selectedChoiceOrder === correctChoiceOrder) return "درست زدی";
    else return "غلط زدی";
  }, [selectedChoiceOrder, correctChoiceOrder]);

  return (
    <>
      <DqModal
        questionModalId={questionModalId}
        setQuestionModalId={setQuestionModalId}
      />
      <div className={styles["question"]}>
        <div className={styles["question__header"]}>
          <span
            className={styles["question__header-number"]}
            onClick={() => setQuestionModalId(questionId || questionID)}
          >
            {questionOrder}
          </span>
          <div className={styles["question__header-level"]}>
            <QLevel level={level} />
            <span>{level === 1 ? "آسان" : level === 2 ? "متوسط" : "سخت"}</span>
          </div>
        </div>
        <div className={styles["question__divider"]} />
        <div className={styles["question__body"]}>
          <div className={styles["question__body-row"]}>
            <div>
              <span>وضعیت گزینه‌ها:</span>
              <span className={styles["question__body-row--status"]}>
                {status}
              </span>
            </div>
            <QStatus
              selectedChoice={selectedChoiceOrder}
              correctChoice={correctChoiceOrder}
            />
          </div>
          <div className={styles["question__body-row"]}>
            <span>موضوع:</span>
            {mainTopic && mainTopic.length > 40 && !isDesktop ? (
              <>
                <div className={styles["question__body-row--info"]}>
                  <span>{truncate(mainTopic, { length: 40 })}</span>
                  <img
                    src={InfoIcon}
                    alt="درس دوم"
                    onClick={() => setShowTooltip((prevState) => !prevState)}
                  />
                </div>
                {showTooltip && (
                  <div className={styles["question__body-row-tooltip"]}>
                    {mainTopic}
                  </div>
                )}
              </>
            ) : mainTopic === null ? (
              <></>
            ) : (
              <div className={styles["question__body-row--info"]}>
                <span>{mainTopic}</span>
              </div>
            )}
          </div>
          <div className={styles["question__body-row"]}>
            <span>مبحث:</span>
            <span>{subTopic}</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResultsQuestion;
